export interface BrowserSpeechRecognitionEvent {
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
}

export interface BrowserSpeechRecognitionErrorEvent {
  error?: string;
}

interface BrowserSpeechRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionConstructor {
  new (): BrowserSpeechRecognition;
}

type SpeechWindow = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

export function browserTranscriptionSupported() {
  if (typeof window === 'undefined') return false;
  const speechWindow = window as SpeechWindow;
  return Boolean(speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition);
}

export function browserSpeechSynthesisSupported() {
  return typeof window !== 'undefined' && Boolean(window.speechSynthesis) && typeof SpeechSynthesisUtterance === 'function';
}

export function browserVoiceSupported() {
  return browserTranscriptionSupported() && browserSpeechSynthesisSupported();
}

function dictationErrorMessage(error?: string) {
  if (error === 'not-allowed' || error === 'service-not-allowed') return 'Microphone permission was denied. You can keep typing.';
  if (error === 'no-speech') return 'No speech was detected. You can keep typing.';
  return 'Dictation stopped unexpectedly. You can keep typing.';
}

export function startBrowserTranscription(
  onTranscript: (text: string) => void,
  onEnd: () => void,
  onError?: (message: string) => void
) {
  const speechWindow = window as SpeechWindow;
  const Constructor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
  if (!Constructor) return null;
  const recognition = new Constructor();
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.lang = navigator.language || 'en-AU';
  recognition.onresult = (event) => {
    const text = Array.from(event.results).map((result) => result[0]?.transcript ?? '').join(' ').trim();
    if (text) onTranscript(text);
  };
  recognition.onerror = (event) => {
    onError?.(dictationErrorMessage(event.error));
    onEnd();
  };
  recognition.onend = onEnd;
  try {
    recognition.start();
  } catch {
    onError?.('Dictation could not start. You can keep typing.');
    onEnd();
    return null;
  }
  return () => recognition.stop();
}

export function speakBrowserReply(text: string, onError?: (message: string) => void) {
  if (!browserSpeechSynthesisSupported() || !text.trim()) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = navigator.language || 'en-AU';
  utterance.rate = 1;
  utterance.onerror = (event) => {
    if (event.error !== 'canceled' && event.error !== 'interrupted') onError?.('Read aloud failed. You can still read the reply.');
  };
  try { window.speechSynthesis.speak(utterance); }
  catch { onError?.('Read aloud could not start. You can still read the reply.'); }
}
